/**
 * Org detail — tabbed view: Overview, Plan, Members, Display, Settings, Flags, Audit.
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, NavLink, Routes, Route, Navigate } from 'react-router-dom';
import { api } from '../../api';
import OrgOverviewTab from './tabs/OrgOverviewTab';
import OrgPlanTab from './tabs/OrgPlanTab';
import OrgDisplayTab from './tabs/OrgDisplayTab';
import OrgMembersTab from './tabs/OrgMembersTab';
import OrgConsultantsTab from './tabs/OrgConsultantsTab';
import OrgSettingsTab from './tabs/OrgSettingsTab';
import OrgFlagsTab from './tabs/OrgFlagsTab';
import OrgAuditTab from './tabs/OrgAuditTab';

export default function OrgDetail({ opsRole }) {
  const { orgId } = useParams();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrg = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`/ops_admin/orgs/${orgId}`);
      setOrg(data.org);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  if (loading) return <p className="loading-text">Loading org...</p>;
  if (error) return <p className="error-text">{error}</p>;
  if (!org) return <p className="error-text">Org not found</p>;

  const displayName = org.is_criminal_justice ? org.id.slice(0, 8) : org.name;
  const basePath = `/ops_admin/orgs/${orgId}`;

  return (
    <div className="ops-org-detail">
      <div className="ops-org-detail-header">
        <h1>{displayName}</h1>
        <div className="org-meta">
          <span className={`badge badge-${org.state}`}>{org.state}</span>
          <span className="org-type">{org.org_type}</span>
          {org.is_criminal_justice && <span className="badge badge-cj">CJ</span>}
        </div>
      </div>

      <nav className="ops-tab-nav">
        {[
          { path: 'overview', label: 'Overview' },
          { path: 'plan', label: 'Plan & Billing' },
          { path: 'display', label: 'Cost Display' },
          { path: 'members', label: 'Members' },
          { path: 'consultants', label: 'Consultants' },
          { path: 'settings', label: 'Settings' },
          { path: 'flags', label: 'Flags' },
          { path: 'audit', label: 'Audit' },
        ].map(({ path, label }) => (
          <NavLink
            key={path}
            to={`${basePath}/${path}`}
            className={({ isActive }) => isActive ? 'ops-tab active' : 'ops-tab'}
          >
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="ops-tab-content">
        <Routes>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<OrgOverviewTab org={org} opsRole={opsRole} onRefresh={fetchOrg} />} />
          <Route path="plan" element={<OrgPlanTab org={org} opsRole={opsRole} />} />
          <Route path="display" element={<OrgDisplayTab org={org} opsRole={opsRole} />} />
          <Route path="members" element={<OrgMembersTab org={org} opsRole={opsRole} />} />
          <Route path="consultants" element={<OrgConsultantsTab org={org} opsRole={opsRole} />} />
          <Route path="settings" element={<OrgSettingsTab org={org} opsRole={opsRole} />} />
          <Route path="flags" element={<OrgFlagsTab org={org} opsRole={opsRole} />} />
          <Route path="audit" element={<OrgAuditTab org={org} />} />
          <Route path="*" element={<Navigate to="overview" replace />} />
        </Routes>
      </div>
    </div>
  );
}
